import React from 'react';
import './RecordingButton.css';

export interface RecordingButtonProps {
  /** Whether recording is currently active */
  isRecording: boolean;
  /** Whether the button should be disabled (e.g., no microphone permission) */
  disabled: boolean;
  /** Callback when button is clicked */
  onToggle: () => void;
  /** Optional additional CSS class */
  className?: string;
}

/**
 * Start/Stop recording button component
 * Toggles between start and stop states based on recording status
 * Disabled when microphone permission is not granted
 */
export const RecordingButton: React.FC<RecordingButtonProps> = ({
  isRecording,
  disabled,
  onToggle,
  className = '',
}) => {
  const buttonClass = [
    'recording-button',
    isRecording ? 'recording-button--recording' : 'recording-button--stopped',
    disabled ? 'recording-button--disabled' : '',
    className,
  ].filter(Boolean).join(' ');

  const buttonText = isRecording ? 'Stop Recording' : 'Start Recording';
  const ariaLabel = disabled 
    ? 'Recording disabled - microphone permission required'
    : `${buttonText} - ${isRecording ? 'Currently recording' : 'Ready to record'}`;

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={onToggle}
      disabled={disabled}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <span className="recording-button__icon">
        {isRecording ? '⏹️' : '🎤'}
      </span>
      <span className="recording-button__text">
        {buttonText}
      </span>
    </button>
  );
};